import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CancelPresentationOutlinedIcon from "@mui/icons-material/CancelPresentationOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type { BillingRecord } from "@services/billing";

const CANCEL_REASONS = [
  "Facility is temporarily closed",
  "Switching to a different plan",
  "Too expensive",
  "Missing features I need",
  "No longer need compliance tracking",
  "Other",
] as const;

type ConfirmCancellationDialogProps = {
  open: boolean;
  record: BillingRecord | null;
  cancelReason: string;
  submitting: boolean;
  onCancelReasonChange: (reason: string) => void;
  onGoBack: () => void;
  onConfirm: () => void;
};

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slash) {
    return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  }
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatShortDate(value: string | null | undefined): string {
  const d = parseDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

export const ConfirmCancellationDialog: React.FC<
  ConfirmCancellationDialogProps
> = ({
  open,
  record,
  cancelReason,
  submitting,
  onCancelReasonChange,
  onGoBack,
  onConfirm,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [reasonMenuOpen, setReasonMenuOpen] = useState(false);

  const plan = record?.selectedPlan || "Plan";
  const frequency = record?.billingFrequency || "Monthly";
  const facilityName = record?.facilityName || "Facility";

  const periodEndRaw =
    record?.planEndDate || record?.planEndDateIso || record?.expiryDate;

  const accessUntil = formatShortDate(periodEndRaw);
  const removedOn = accessUntil;

  const archivedUntil = useMemo(() => {
    const end = parseDate(periodEndRaw);
    if (!end) return "—";
    return addMonths(end, 6).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [periodEndRaw]);

  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setReasonMenuOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (submitting) {
      setReasonMenuOpen(false);
    }
  }, [submitting]);

  const summaryRows = [
    { label: "Facility", value: facilityName },
    { label: "Plan", value: `${plan} (${frequency})` },
    { label: "Users retain current access until", value: accessUntil },
    { label: "Facility removed on", value: removedOn },
    { label: "Data archived until", value: archivedUntil },
    { label: "Notification sent to", value: "All facility admins" },
  ];

  const handleClose = () => {
    setAcknowledged(false);
    onGoBack();
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) handleClose();
      }}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          fontFamily: '"Rethink Sans", sans-serif',
          maxHeight: "92vh",
          overflowY: "auto",
          position: "relative",
        },
      }}
    >
      {submitting && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.72)",
            borderRadius: "12px",
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={36} sx={{ color: "#0a829d" }} />
        </Box>
      )}
      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          overflow: "visible",
          pointerEvents: submitting ? "none" : "auto",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#065b70",
              lineHeight: 1.3,
              mb: 0.75,
            }}
          >
            Confirm cancellation
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#525252", lineHeight: 1.5 }}>
            Let us know why you&apos;re leaving, then review the summary and
            confirm below.
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#f3fdff",
            border: "1px solid #d8eff4",
            borderRadius: "10px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #e5f5f8",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#262626",
              }}
            >
              Why are you cancelling?{" "}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#737373",
              }}
            >
              (optional)
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: "#ffffff",
              borderBottomLeftRadius: "10px",
              borderBottomRightRadius: "10px",
            }}
          >
            <Box
              role="button"
              tabIndex={submitting ? -1 : 0}
              aria-expanded={reasonMenuOpen}
              aria-disabled={submitting}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (submitting) return;
                setReasonMenuOpen((prev) => !prev);
              }}
              onKeyDown={(e) => {
                if (submitting) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setReasonMenuOpen((prev) => !prev);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: 2,
                py: 1.5,
                cursor: submitting ? "default" : "pointer",
                borderBottom: reasonMenuOpen ? "1px solid #e5f5f8" : "none",
                userSelect: "none",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: cancelReason ? "#262626" : "#99a3a9",
                  lineHeight: 1.4,
                }}
              >
                {cancelReason || "Choose Reason"}
              </Typography>
              {reasonMenuOpen ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 20, color: "#737373" }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: 20, color: "#737373" }} />
              )}
            </Box>

            <Collapse in={reasonMenuOpen} timeout="auto" unmountOnExit={false}>
              <Box sx={{ py: 0.5, borderTop: "1px solid #e5f5f8" }}>
                {CANCEL_REASONS.map((reason) => (
                  <Box
                    key={reason}
                    role="button"
                    tabIndex={0}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (submitting) return;
                      onCancelReasonChange(reason);
                      setReasonMenuOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (submitting) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancelReasonChange(reason);
                        setReasonMenuOpen(false);
                      }
                    }}
                    sx={{
                      px: 2,
                      py: 1.25,
                      fontSize: "14px",
                      color: "#262626",
                      lineHeight: 1.4,
                      cursor: "pointer",
                      backgroundColor:
                        cancelReason === reason ? "#f3fdff" : "transparent",
                      "&:hover": {
                        backgroundColor: "#f8fdfe",
                      },
                    }}
                  >
                    {reason}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        </Box>

        <Box
          sx={{
            border: "1px solid #d8eff4",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {summaryRows.map((row, index) => (
            <Box
              key={row.label}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 0.25, sm: 2 },
                px: 2,
                py: 1.25,
                backgroundColor: index % 2 === 0 ? "#f8fdfe" : "#ffffff",
                borderBottom:
                  index < summaryRows.length - 1 ? "1px solid #e5f5f8" : "none",
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#737373" }}>
                {row.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#262626",
                  textAlign: { xs: "left", sm: "right" },
                }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            backgroundColor: "#f3fdff",
            border: "1px solid #99d4e1",
            borderRadius: "8px",
            px: 1.5,
            py: 1,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                disabled={submitting}
                onChange={(e) => setAcknowledged(e.target.checked)}
                sx={{
                  color: "#0a829d",
                  "&.Mui-checked": { color: "#0a829d" },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "13px", color: "#065b70", lineHeight: 1.45 }}>
                I understand that all users of {facilityName} will lose their
                current access on {accessUntil} and that facility data will be
                permanently deleted after {archivedUntil} if I do not resubscribe.
              </Typography>
            }
            sx={{ alignItems: "flex-start", m: 0 }}
            disabled={submitting}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "space-between",
            pt: 0.5,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={submitting}
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              color: "#404040",
              borderColor: "#b3b3b3",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              px: 3,
              py: 1.1,
              flex: { xs: "1 1 100%", sm: "0 1 auto" },
              "&:hover": {
                borderColor: "#8a8a8a",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Go Back
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={handleConfirm}
            disabled={!acknowledged || submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} sx={{ color: "#a4262c" }} />
              ) : (
                <CancelPresentationOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: acknowledged ? "#a4262c" : "#9e9e9e",
                  }}
                />
              )
            }
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              borderRadius: "8px",
              px: 3,
              py: 1.1,
              flex: { xs: "1 1 100%", sm: "0 1 auto" },
              boxShadow: "none",
              ...(acknowledged && !submitting
                ? {
                    borderColor: "#d13438",
                    color: "#a4262c",
                    backgroundColor: "#fde7e9",
                    "&:hover": {
                      backgroundColor: "#f9d2d6",
                      borderColor: "#a4262c",
                    },
                  }
                : {
                    borderColor: "#e0e0e0",
                    color: "#9e9e9e",
                    backgroundColor: "#f5f5f5",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#e0e0e0",
                    },
                  }),
              "&.Mui-disabled": {
                borderColor: "#e0e0e0",
                color: "#9e9e9e",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {submitting ? "Cancelling..." : "Confirm Cancellation"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmCancellationDialog;
